import { autorun, makeAutoObservable } from 'mobx'

import { DefaultAppConfig } from '../constants/DefaultConfig'

import {
  AccountStore,
  AnalyticsStore,
  AppDataStore,
  BeatStore,
  BudgetsStore,
  CashflowStore,
  CategoryStore,
  ConnectStore,
  DebtsStore,
  FinstrongStore,
  GlobalCopyStore,
  GlobalUiStore,
  GoalStore,
  HelpStore,
  HoldingStore,
  InsightsV2Store,
  MerchantStore,
  NetWorthStore,
  NotificationStore,
  RecurringTransactionsStore,
  SettingsStore,
  TransactionStore,
  TrendsStore,
  UserStore,
} from '../stores'

import type {
  Account,
  Category,
  Institution,
  Member,
  AppConfig,
  AppData,
  Beat,
  GlobalCaptureException,
  GlobalErrorHandler,
  MerchantBudget,
  Tag,
  Tagging,
  Transaction,
  TransactionRule,
  UserFeature,
} from '../types'

interface LoadDataProps {
  accounts?: Account[]
  appData?: AppData
  associatedBeats?: Beat[]
  categories?: Category[]
  institutions?: Institution[]
  members?: Member[]
  merchantBudgets?: MerchantBudget[]
  taggings?: Tagging[]
  tags?: Tag[]
  transactionRules?: TransactionRule[]
  transactions?: Transaction[]
  userFeatures?: UserFeature[]
}

type OnStoreUpdate = (update: ObservedUpdate) => void
type ObservedUpdate = {
  transactions: Transaction[]
}
export class GlobalStore {
  // Global properties
  appConfig: AppConfig = DefaultAppConfig
  endpoint: string = '/'
  sessionToken: string = ''
  onCaptureException?: GlobalCaptureException
  onError?: GlobalErrorHandler
  onStoreUpdate?: OnStoreUpdate

  // Data Stores
  accountStore: AccountStore
  appDataStore: AppDataStore
  analyticsStore: AnalyticsStore
  beatStore: BeatStore
  budgetsStore: BudgetsStore
  cashflowStore: CashflowStore
  categoryStore: CategoryStore
  connectStore: ConnectStore
  copyStore: GlobalCopyStore
  debtsStore: DebtsStore
  finstrongStore: FinstrongStore
  goalStore: GoalStore
  helpStore: HelpStore
  holdingStore: HoldingStore
  merchantStore: MerchantStore
  netWorthStore: NetWorthStore
  notificationStore: NotificationStore
  recurringTransactionsStore: RecurringTransactionsStore
  settingsStore: SettingsStore
  transactionStore: TransactionStore
  trendsStore: TrendsStore
  userStore: UserStore

  // UI Stores
  globalUiStore: GlobalUiStore
  insightsV2Store: InsightsV2Store

  constructor(
    appConfig: AppConfig = DefaultAppConfig,
    endpoint: string = '/',
    onError?: GlobalErrorHandler,
    onStoreUpdate?: OnStoreUpdate,
    onCaptureException?: GlobalCaptureException,
  ) {
    this.appConfig = appConfig
    this.endpoint = endpoint
    this.sessionToken = appConfig.options.session_token ?? ''
    this.onCaptureException = onCaptureException
    this.onError = onError
    this.onStoreUpdate = onStoreUpdate

    this.accountStore = new AccountStore(this)
    this.appDataStore = new AppDataStore(this)
    this.analyticsStore = new AnalyticsStore(this)
    this.beatStore = new BeatStore(this)
    this.budgetsStore = new BudgetsStore(this)
    this.cashflowStore = new CashflowStore(this)
    this.categoryStore = new CategoryStore(this)
    this.connectStore = new ConnectStore(this)
    this.copyStore = new GlobalCopyStore(this)
    this.debtsStore = new DebtsStore(this)
    this.finstrongStore = new FinstrongStore(this)
    this.goalStore = new GoalStore(this)
    this.helpStore = new HelpStore(this)
    this.holdingStore = new HoldingStore(this)
    this.merchantStore = new MerchantStore(this)
    this.netWorthStore = new NetWorthStore(this)
    this.notificationStore = new NotificationStore(this)
    this.recurringTransactionsStore = new RecurringTransactionsStore(this)
    this.settingsStore = new SettingsStore(this)
    this.transactionStore = new TransactionStore(this)
    this.trendsStore = new TrendsStore(this)
    this.userStore = new UserStore(this)

    this.globalUiStore = new GlobalUiStore(this)
    this.insightsV2Store = new InsightsV2Store(this)

    makeAutoObservable(this)

    // detects a change based on an observed value
    autorun(() => {
      // destructure or assign new variables to observe changes to them
      // in this case we are observing changes to `transactions`
      const { transactions } = this.transactionStore
      this.emitUpdate({
        transactions,
      })
    })
  }

  get isInitialized() {
    return this.globalUiStore.isInitialized
  }

  captureException = (error: unknown, context?: Record<string, string>) => {
    this.onCaptureException?.(error, context)
  }

  // emits change triggered in the autorun
  emitUpdate = (change: ObservedUpdate) => {
    this.onStoreUpdate?.(change)
  }

  loadConfigData = async () => {
    await Promise.all([
      // Load copy, config and user data
      this.copyStore.loadCopy(),
      this.appDataStore.loadAppData(),
      this.accountStore.refreshMembers(),
      this.userStore.loadUserFeatures(),
    ])
  }

  loadData = async ({
    accounts,
    appData,
    associatedBeats,
    categories,
    institutions,
    transactionRules,
    transactions,
    userFeatures,
    merchantBudgets,
  }: LoadDataProps = {}) => {
    if (!appData || !userFeatures) {
      await this.loadConfigData()
    }

    // Keep for backwards compatibility for Insights
    if (appData) {
      this.appDataStore.setAppData(appData)
    }

    if (userFeatures) {
      this.userStore.setUserFeatures(userFeatures)
    }

    if (accounts) {
      await this.accountStore.loadMembers()
      if (institutions) {
        this.accountStore.setInstitutions(institutions)
      }
      this.accountStore.setAccounts(accounts)
    }

    if (associatedBeats) {
      this.transactionStore.setAssociatedBeats(associatedBeats)
    }

    if (categories) {
      this.categoryStore.setCategories(categories)
    }

    if (transactions) {
      this.transactionStore.setTransactions(transactions)
    }

    if (transactionRules) {
      this.transactionStore.setTransactionRules(transactionRules)
    }

    if (merchantBudgets) {
      this.budgetsStore.setMerchantBudgets(merchantBudgets)
    }
  }
}
